// Pure helpers for the conversion-sheet grid (SPEC-068). No React, no DOM.

// Backend header keys are camelCase; snake_case is accepted for older payloads.
const toCamel = (key) => key.replace(/_([a-z])/g, (_, c) => c.toUpperCase())

const NUMERIC_COLUMNS = new Set(["leadTimeToLatestArrival", "leadTimeToDeadline", "leadTimeToEarliestStart"])
const REQUIRED_COLUMNS = new Set(["limsAnalysisName", "plannertechAnalysisName", "leadTimeToDeadline", "projectTemplateName"])
// Typed LIMS columns, checked the way the server's schema checks them (SPEC-069 addendum).
const WHOLE_NUMBER_COLUMNS = new Set(["priority", "numberOfObservations"])
// A sample's priority must be 1, 2 or 3 (database check on sample_observation). The conversion
// sheet itself accepts any whole number, but one row outside 1-3 makes the automatic import fail
// for every sample in the upload, so the grid refuses it here.
const PRIORITIES = new Set([1, 2, 3])
const BOOLEAN_COLUMNS = new Set(["exclusive"])
// Exactly the spellings the server's Boolean field accepts (marshmallow truthy/falsy sets).
const BOOLEAN_TEXT = new Set(
  ["true", "false", "t", "f", "yes", "no", "y", "n", "on", "off"].flatMap((w) => [
    w,
    w.toUpperCase(),
    w[0].toUpperCase() + w.slice(1),
  ]).concat(["1", "0"])
)
// The LIMS columns that identify an incoming row are shown first, in this order.
const LIMS_EXTERNAL_ORDER = ["limsAnalysisName", "limsMaterialNumber", "limsProductDescription", "limsCustomerName"]

export const isNumericColumn = (key) => NUMERIC_COLUMNS.has(toCamel(key))
export const isRequiredColumn = (key) => REQUIRED_COLUMNS.has(toCamel(key))

export function orderHeaders(headers, sheetType) {
  if (sheetType !== "Sample") return headers
  const rank = (header) => {
    const i = LIMS_EXTERNAL_ORDER.indexOf(toCamel(header.key))
    return i === -1 ? LIMS_EXTERNAL_ORDER.length : i
  }
  // Array.prototype.sort is stable, so the remaining columns keep the backend's order.
  return [...headers].sort((a, b) => rank(a) - rank(b))
}

export const isEmptyValue = (value) => value == null || String(value).trim() === ""

// Text shown in a cell. 0 and false are values, not blanks.
export const cellText = (value) => (value == null ? "" : String(value))

export function isCellValid(key, value) {
  if (isEmptyValue(value)) return !isRequiredColumn(key)
  const column = toCamel(key)
  if (NUMERIC_COLUMNS.has(column)) {
    const number = Number(value)
    return Number.isFinite(number) && number >= 0
  }
  if (WHOLE_NUMBER_COLUMNS.has(column)) {
    const whole = typeof value === "number" ? Number.isInteger(value) : /^\s*[+-]?\d+\s*$/.test(value)
    return whole && (column !== "priority" || PRIORITIES.has(Number(value)))
  }
  if (BOOLEAN_COLUMNS.has(column)) {
    return typeof value === "boolean" || BOOLEAN_TEXT.has(String(value))
  }
  return true
}

// Numeric input accepts a decimal comma and ignores stray characters such as a " days" suffix.
// A minus sign is kept so a negative value is flagged instead of silently turning positive.
const normaliseNumeric = (text) => text.replace(/,/g, ".").replace(/[^\d.-]/g, "")

// Returns a copy of `row` with the user's text for `key` applied. An empty optional cell is
// removed from the row; an empty required cell is kept as "" so validation can flag it.
export function applyCellText(row, key, rawText) {
  let text = rawText.trim()
  if (isNumericColumn(key)) text = normaliseNumeric(text)
  const next = { ...row }
  if (text === "" && !isRequiredColumn(key)) {
    delete next[key]
  } else if (isNumericColumn(key) && text !== "" && isCellValid(key, text)) {
    next[key] = parseFloat(text)
  } else {
    next[key] = text
  }
  return next
}

// eslint-disable-next-line no-control-regex
const LINE_BREAK_OR_CONTROL = /[\x00-\x1F\x7F-\x9F\u2028\u2029]/g

// Reads a quoted cell starting after its opening quote. Returns the raw cell text (doubled
// quotes unescaped) and the index after the closing quote.
function readQuoted(source, from) {
  let text = ""
  let i = from
  while (i < source.length) {
    const close = source.indexOf('"', i)
    if (close === -1) return [text + source.slice(i), source.length]
    text += source.slice(i, close)
    if (source[close + 1] !== '"') return [text, close + 1]
    text += '"'
    i = close + 2
  }
  return [text, i]
}

// Splits clipboard text (tab-separated, as copied from Excel) into rows of cells. Excel wraps a
// cell containing line breaks or tabs in double quotes; such a cell stays one cell.
export function parseClipboard(text) {
  let source = text.replace(/\r\n?/g, "\n")
  while (source.endsWith("\n")) source = source.slice(0, -1)
  const rows = [[]]
  let cell = ""
  let i = 0
  while (i < source.length) {
    const ch = source[i]
    if (ch === '"' && cell.trim() === "") {
      const [quoted, next] = readQuoted(source, i + 1)
      cell = quoted.replace(LINE_BREAK_OR_CONTROL, " ")
      i = next
      continue
    }
    if (ch === "\t" || ch === "\n") {
      rows[rows.length - 1].push(cell)
      if (ch === "\n") rows.push([])
      cell = ""
    } else {
      cell += ch
    }
    i++
  }
  rows[rows.length - 1].push(cell)
  return rows
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" })

// Blank cells sort last in both directions; numbers compare numerically, text naturally.
export function compareValues(a, b) {
  const aEmpty = isEmptyValue(a)
  const bEmpty = isEmptyValue(b)
  if (aEmpty && bEmpty) return 0
  if (aEmpty) return 1
  if (bEmpty) return -1
  if (typeof a === "number" && typeof b === "number") return a - b
  return collator.compare(String(a), String(b))
}

// Saved rows sorted by `sort` (stable), followed by new rows in the order they were added.
export function displayOrder(rows, sort) {
  const saved = rows.filter((row) => !row.isNew)
  const added = rows.filter((row) => row.isNew)
  if (sort) {
    const direction = sort.desc ? -1 : 1
    saved.sort((a, b) => {
      const result = compareValues(a.data[sort.key], b.data[sort.key])
      // Blanks stay last regardless of direction.
      return isEmptyValue(a.data[sort.key]) || isEmptyValue(b.data[sort.key]) ? result : result * direction
    })
  }
  return [...saved, ...added]
}

// Search: does any shown column's text contain the query (case-insensitive)?
export function rowMatches(data, headers, query) {
  const needle = query.trim().toLowerCase()
  return headers.some((header) => cellText(data[header.key]).toLowerCase().includes(needle))
}

// Keys of the saved rows matching the query, or null when there is no query. Taken once per
// query, so a row being edited never disappears from under the cursor.
export function matchingKeys(rows, headers, query) {
  if (!query.trim()) return null
  return new Set(rows.filter((row) => !row.isNew && rowMatches(row.data, headers, query)).map((row) => row.key))
}

// The rows on screen: sorted, filtered by the search snapshot; rows added this session always show.
export function visibleRows(rows, sort, filterKeys) {
  const ordered = displayOrder(rows, sort)
  return filterKeys ? ordered.filter((row) => row.isNew || filterKeys.has(row.key)) : ordered
}

// Header click cycles ascending → descending → unsorted.
export function nextSort(sort, key) {
  if (!sort || sort.key !== key) return { key, desc: false }
  return sort.desc ? null : { key, desc: true }
}

export const exportRows = (headers, rows) => [
  headers.map((header) => header.name),
  ...rows.map((row) => headers.map((header) => row.data[header.key] ?? "")),
]
